/* eslint-disable react/jsx-no-useless-fragment */
import * as React from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { CUSTOMER_KEY, DURATION_KEY } from '../constants';
import MultiSelectFilter from '../Filter/MultiSelectFilter';
import RangeDurationFilter from '../Filter/RangeDurationFilter';
import RangeSelectFilter from '../Filter/RangeSelectFilter';
import SimpleSelectFilter from '../Filter/SimpleSelectFilter';
import useFilter from '../hooks/useFilter';
import { IFilterTripsOverview, ITripsOverviewTableData } from '../interfaces';
import {
  filterForRequestAtom,
  hasMoreAtom,
  offsetAtom,
  rideBookDataAtom,
  rideBookFilterAtom,
  rideItemIdsAtom,
  rideItemTimestampsAtom,
  summaryItemIndexesAtom,
} from '../TripsOverviewState';
import useAnalytics from '../../../hooks/useAnalytics';

interface IFilterChipsProps {
  id: string;
}

function FilterChips(props: IFilterChipsProps): JSX.Element {
  const { updateFilterItemsState } = useFilter();

  const [tableActionsFilters, setTableActionsFilters] = useRecoilState(
    rideBookFilterAtom(props.id)
  );
  const [filterForRequest, setFilterForRequest] =
    useRecoilState<IFilterTripsOverview>(filterForRequestAtom);
  const setOffset = useSetRecoilState<number>(offsetAtom);
  const setTableData = useSetRecoilState<ITripsOverviewTableData | null>(rideBookDataAtom);
  const setRideItemIds = useSetRecoilState<string[]>(rideItemIdsAtom);
  const setHasMore = useSetRecoilState<boolean>(hasMoreAtom);
  const setRideItemTimestamps = useSetRecoilState<string[]>(rideItemTimestampsAtom);
  const setSummaryItemIndexes = useSetRecoilState<number[]>(summaryItemIndexesAtom);
  const { trackEvent } = useAnalytics();

  const handleRemoveChips = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.stopPropagation();
    const { id } = event.currentTarget;
    trackEvent(['Ride Book', 'Filter', `Ride book remove filter ${id}`]);

    const data = { ...filterForRequest };

    delete data[props.id];

    setFilterForRequest(data);
    setOffset(0);
    setTableData(null);
    setRideItemTimestamps([]);
    setSummaryItemIndexes([]);
    setRideItemIds([]);
    setHasMore(true);

    setTableActionsFilters(
      (tableActionFilter) =>
        tableActionFilter && {
          ...tableActionFilter,
          id: props.id,
          isFilterActive: false,
          isActionChipsOpen: false,
          isOpen: false,
          values: null,
        }
    );
  };

  const handleOpenFilter = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.preventDefault();

    if (tableActionsFilters && !tableActionsFilters.isActionChipsOpen) {
      if (tableActionsFilters) {
        trackEvent(['Ride Book', 'Filter', `Ride book open filter ${props.id}`]);
        updateFilterItemsState([
          {
            ...tableActionsFilters,
            id: props.id,
            isOpen: false,
            isActionChipsOpen: true,
          },
        ]);
      }
    }
  };

  return (
    <>
      {tableActionsFilters && tableActionsFilters.values && (
        <div
          id={props.id}
          className="label label-primary mr-1 mb-1 label--filter dropdown-toggle"
          onClick={handleOpenFilter}
          style={{
            position: 'relative',
            whiteSpace: 'nowrap',
            textAlign: 'left',
          }}
          data-cy={`ride-book-filter-chips-${props.id}`}
        >
          {`${tableActionsFilters.values?.title}: ${tableActionsFilters.values?.item}`}

          <i
            id={props.id}
            className="cmd-icon cmd-icon-remove-sm"
            style={{
              paddingLeft: '8px',
              cursor: 'pointer',
            }}
            onClick={handleRemoveChips}
            data-cy="ride-book-filter-chips-close"
          />

          {tableActionsFilters.isActionChipsOpen &&
            tableActionsFilters.values &&
            tableActionsFilters.values?.type === 'rangeSelect' &&
            props.id !== DURATION_KEY && (
              <div
                id="range-select"
                style={{ position: 'absolute', left: '0px', top: '33px', zIndex: 200 }}
                className="text-black"
              >
                <div className="open">
                  <RangeSelectFilter
                    id={props.id}
                    title={tableActionsFilters.values.title}
                    valueFrom={tableActionsFilters.values.min}
                    valueTo={tableActionsFilters.values.max}
                    defaultMinValue={
                      tableActionsFilters.data && tableActionsFilters.data.minValue
                        ? tableActionsFilters.data.minValue
                        : '0'
                    }
                    defaultMaxValue={
                      tableActionsFilters.data && tableActionsFilters.data.maxValue
                        ? tableActionsFilters.data.maxValue
                        : '9999999'
                    }
                    note={tableActionsFilters.values.unit}
                    suffix={tableActionsFilters.values.suffixInput}
                  />
                </div>
              </div>
            )}

          {tableActionsFilters.isActionChipsOpen &&
            tableActionsFilters.values?.type === 'simpleSelect' &&
            tableActionsFilters.data && (
              <div style={{ position: 'absolute', left: '0px', bottom: '0px' }}>
                <div className="label open">
                  <SimpleSelectFilter
                    id={props.id}
                    filterValues={tableActionsFilters.data}
                    search={props.id === CUSTOMER_KEY}
                    title={tableActionsFilters.values.title}
                  />
                </div>
              </div>
            )}
          {tableActionsFilters.isActionChipsOpen &&
            tableActionsFilters.values?.type === 'rangeSelect' &&
            props.id === DURATION_KEY && (
              <div
                style={{ position: 'absolute', left: '0px', top: '33px', zIndex: 200 }}
                className="text-black"
              >
                <RangeDurationFilter id={props.id} />
              </div>
            )}
          {tableActionsFilters.isActionChipsOpen &&
            tableActionsFilters.values?.type === 'multiSelect' &&
            tableActionsFilters.data && (
              <div style={{ position: 'absolute', left: '0px', top: '30px', zIndex: 999999999 }}>
                <div className="open">
                  <MultiSelectFilter
                    id={props.id}
                    filterValues={tableActionsFilters.data}
                    title={tableActionsFilters.values.title}
                  />
                </div>
              </div>
            )}
        </div>
      )}
    </>
  );
}

export default React.memo(FilterChips);
